import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Metric, MetricsMutationsOptions } from '../types/metricTypes';
import { 
  addPricingMetricMutation, 
  AddPricingMetricInput 
} from '@/graphql/mutations/addPricingMetric';
import { 
  updatePricingMetricMutation, 
  UpdatePricingMetricInput 
} from '@/graphql/mutations/updatePricingMetric';
import { deletePricingMetricMutation } from '@/graphql/mutations/deletePricingMetric';
import {
  addPricingMetricInRedux,
  updatePricingMetricInRedux,
  removePricingMetricFromRedux
} from "@/store/actions/metricActions";

/**
 * Custom hook to handle all pricing metric mutations (add, update, delete)
 */
export const useMetricsMutations = (options: MetricsMutationsOptions) => {
  const dispatch = useDispatch();
  const { slug } = useParams();

  // Get current company data from Redux
  const company = useSelector((state: any) => {
    const graphqlData = state.graphql?.data;
    return graphqlData?.company?.slug === slug ? graphqlData.company : null;
  });

  // Get the current page based on subslug
  const currentPage = company?.pages?.find((p: any) => 
    options.pageSubslug && options.pageSubslug !== 'main' 
      ? p.subslug === options.pageSubslug
      : !p.subslug || p.subslug === ''
  );

  // Handler for adding a new metric
  const handleAddMetric = async () => {
    if (!options.newMetricName.trim() || !currentPage) {
      toast.error('Please enter a metric name');
      return;
    }

    options.setIsAddingMetric(true);

    try {
      console.log('🆕 Adding new metric via GraphQL');

      // Create the input object for the mutation
      const input: AddPricingMetricInput = {
        display_name: options.newMetricName,
        modality: options.newMetricModality,
        page_id: currentPage.id
      };

      // Call the GraphQL mutation
      const response = await addPricingMetricMutation(input);

      if (response?.data?.insert_pricing_metrics_one) {
        const newMetric = response.data.insert_pricing_metrics_one;
        
        // Update Redux store with the new metric
        dispatch(addPricingMetricInRedux({
          id: newMetric.id,
          display_name: newMetric.display_name,
          modality: newMetric.modality,
          page_id: currentPage.id
        }) as any);

        // Show success message
        toast.success('Metric added successfully');

        // Refresh company data if needed
        if (options.refreshCompanyData) {
          await options.refreshCompanyData();
        }

        // Reset state
        options.setNewMetricName('');
        options.setShowAddMetricModal(false);
      }
    } catch (error) {
      console.error('Error adding metric:', error);
      toast.error('Failed to add metric');
    } finally {
      options.setIsAddingMetric(false);
    }
  };

  // Handler for updating an existing metric
  const handleMetricUpdated = async (updatedMetric: Metric) => {
    try {
      if (!currentPage) {
        throw new Error('No page found for metric update');
      }

      console.log(`✏️ Updating metric via GraphQL: ${updatedMetric.id}`);
      
      // Create the input object for the mutation
      const input: UpdatePricingMetricInput = {
        id: updatedMetric.id,
        display_name: updatedMetric.display_name,
        modality: updatedMetric.modality as string
      };

      // Call the GraphQL mutation
      const response = await updatePricingMetricMutation(input);
      
      const updatedMetricFromServer = response?.updatePricingMetric;
      
      if (updatedMetricFromServer) {
        console.log('✅ Successfully updated metric in server, updating Redux store');
        
        // Update Redux store with the updated metric, ensure all fields are properly passed
        dispatch(updatePricingMetricInRedux({
          id: updatedMetric.id,
          display_name: updatedMetric.display_name,
          modality: updatedMetric.modality,
          page_id: currentPage.id,
          sample_values: updatedMetric.sample_values || []
          // Note: associatedPlans is not included in PricingMetric type
        }) as any);
        
        // Show success message
        toast.success('Metric updated successfully');
        
        // Always refresh company data after an edit to ensure UI is updated
        if (options.refreshCompanyData) {
          console.log('🔄 Refreshing company data to update UI');
          await options.refreshCompanyData();
        }
      }
      
      // Reset state
      options.setShowEditMetricModal(false);
      options.setMetricToEdit(null);
    } catch (error) {
      console.error('Error updating metric:', error);
      toast.error('Failed to update metric');
    }
  };

  // Handler for deleting a metric
  const handleMetricDeleted = async (metricId: string) => {
    if (!metricId) {
      toast.error('No metric ID provided');
      return;
    }

    try {
      console.log(`🗑️ Deleting metric via GraphQL: ${metricId}`);
      
      // Call the GraphQL mutation to delete the metric
      await deletePricingMetricMutation({ id: metricId });
      
      // Update Redux store
      dispatch(removePricingMetricFromRedux(metricId) as any);
      
      // Show success message
      toast.success('Metric deleted successfully');
      
      // Refresh company data if needed
      if (options.refreshCompanyData) {
        await options.refreshCompanyData();
      }
      
      // Reset state
      options.setMetricToDelete(null);
      options.setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting metric:', error);
      toast.error('Failed to delete metric');
    }
  };
  
  return {
    handleAddMetric,
    handleMetricUpdated,
    handleMetricDeleted
  };
};
